import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { LIST_INQUIRIES } from '../../graphql/inquiries';
import type { InquiryStatus, ListInquiriesResponse } from '../../types/inquiry';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export function InquiryQueueWidget() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [allInquiries, setAllInquiries] = useState<any[]>([]);
  const [isAutoLoading, setIsAutoLoading] = useState(false);

  const { data, loading, error, refetch, fetchMore } = useQuery<ListInquiriesResponse>(LIST_INQUIRIES, {
    variables: {
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      limit: 100, // Fetch all inquiries
    },
    fetchPolicy: 'network-only', // Always fetch fresh data from the server
  });

  const loadAllPagesAuto = useCallback(async (initialNextToken: string, initialItems: any[]) => {
    setIsAutoLoading(true);
    try {
      let nextToken: string | undefined = initialNextToken;
      let accumulated = [...initialItems];
      let pageCount = 1;
      
      while (nextToken) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fetchMoreResult: any = await fetchMore({
          variables: {
            status: statusFilter === 'ALL' ? undefined : statusFilter,
            limit: 100,
            nextToken,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            return fetchMoreResult;
          },
        });
        
        const newItems = fetchMoreResult.data?.listInquiries?.items || [];
        const newNextToken = fetchMoreResult.data?.listInquiries?.nextToken;
        
        accumulated = [...accumulated, ...newItems];
        nextToken = newNextToken as string | undefined;
        pageCount++;
        
        // Safety check to prevent infinite loops
        if (pageCount > 10) {
          break;
        }
      }
      
      setAllInquiries(accumulated);
    } catch (err) {
      console.error('Error auto-loading pages:', err);
    } finally {
      setIsAutoLoading(false);
    }
  }, [fetchMore, statusFilter]);

  // Auto-load all pages when data changes
  useEffect(() => {
    if (data?.listInquiries) {
      const items = data.listInquiries.items || [];
      const nextToken = data.listInquiries.nextToken;
      
      setAllInquiries(items);
      
      if (nextToken) {
        loadAllPagesAuto(nextToken, items);
      }
    }
  }, [data, loadAllPagesAuto]);

  // Subscription disabled - using polling instead
  // Real-time updates via WebSocket subscriptions require additional Apollo configuration
  // const { data: subscriptionData } = useSubscription(ON_INQUIRY_CREATED);
  // useEffect(() => {
  //   if (subscriptionData?.onInquiryCreated) {
  //     setShowNotification(true);
  //     setTimeout(() => setShowNotification(false), 5000);
  //     refetch();
  //   }
  // }, [subscriptionData, refetch]);

  const inquiries = allInquiries;

  const filteredInquiries = inquiries
    .filter(inquiry => {
      // Exclude ACCEPTED inquiries from the queue (they've been converted to cases)
      if (inquiry.status === 'ACCEPTED') return false;
      
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        inquiry.firstName.toLowerCase().includes(query) ||
        inquiry.lastName.toLowerCase().includes(query) ||
        inquiry.email.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      // Sort by lead score (highest first)
      if (b.leadScore !== a.leadScore) {
        return b.leadScore - a.leadScore;
      }
      // Then by creation date (most recent first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }); // Show all inquiries, no slice

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-red-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-gray-600';
  };

  const getStatusBadgeVariant = (status: InquiryStatus): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'info'> = {
      NEW: 'info',
      REVIEWING: 'warning',
      ACCEPTED: 'success',
      REJECTED: 'default',
    };
    return variants[status] || 'default';
  };

  if (loading && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Inquiry Queue</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Inquiry Queue</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Error loading inquiries</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="inquiry-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Inquiry Queue
            {isAutoLoading && <span className="ml-2 text-sm text-blue-600">(loading...)</span>}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Quick Filters */}
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InquiryStatus | 'ALL')}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="ALL">All</option>
              <option value="NEW">New</option>
              <option value="REVIEWING">Reviewing</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          {/* Inquiry List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredInquiries.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No inquiries found</p>
            ) : (
              filteredInquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                  className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">
                          {inquiry.firstName} {inquiry.lastName}
                        </span>
                        <Badge variant={getStatusBadgeVariant(inquiry.status)} className="text-xs">
                          {inquiry.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{inquiry.incidentType}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-semibold ${getLeadScoreColor(inquiry.leadScore)}`}>
                          Score: {inquiry.leadScore}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(inquiry.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
